import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ringBorder } from '../portalkit/tokens';
import ProfileAnthem from '../ProfileAnthem';
import MediaViewer from '../MediaViewer';
import useContainerWidth from '../../hooks/useContainerWidth';
import { geom, tilePad, MOBILE_RATIO, desktopLayout, mobileLayout } from '../../utils/portalCover';
import { SENSITIVE_CHIP, genderOnPage } from '../../utils/portalPageModel';
import { BRAND_DOMAIN } from '../../utils/brand';

/*
 * PortalPage — the public Portal Page, design_handoff_portal_profile_editor "Profile Page v2"
 * (Oct 2 2026). Black canvas; #111 panels with the #1F1F1F inset ring; pill tabs with the party
 * ring; a photo cover whose layout is set separately for desktop and phone.
 *
 * It breaks on its OWN width (not the viewport's): phone < 900, two About columns ≥ 1000, the
 * 320px rail ≥ 1200. The editor renders it at 1280 / 390 inside a scaled box through `forceWidth`.
 * Everything it shows comes in as props — `data` is the page object from utils/portalPageModel,
 * the lists are the profile's events and clubs, `actions` are the identity buttons the owning
 * page decides on (MESSAGE / + ADD FRIEND / INTRODUCE, EDIT PAGE / SHARE, LOG IN / SIGN UP).
 */

const SERIF = "'Times New Roman', Times, serif";
const ON = ringBorder('#000');
const COPY_LINE = 'Find your party. Find your people. Find your person.';

const Icon = {
  back: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>,
  pin: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M12 21s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12z" /><circle cx="12" cy="9" r="2.5" /></svg>,
  cal: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M3 5h18v16H3zM3 10h18M8 3v4M16 3v4" /></svg>,
  ig: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><path d="M17.5 6.5h.01" /></svg>,
  tt: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M14 4v11a4 4 0 1 1-4-4" /><path d="M14 4c0 3 2.5 5 5 5" /></svg>,
  work: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><rect x="3" y="7" width="18" height="13" /><path d="M8 7V4h8v3M3 12h18" /></svg>,
  prev: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>,
  next: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>,
  play: <svg width="12" height="12" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true"><path d="M2 1l7 4-7 4z" /></svg>,
};

const fmtEventDate = (dt) => (dt ? new Date(dt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase() : '');
export const eventMeta = (e) => [fmtEventDate(e.dateTime), e.locationCity, e.venueName].filter(Boolean).join(' · ');
const clubMeta = (c) => [c.locationCity, Number.isFinite(c.memberCount) ? `${c.memberCount} MEMBER${c.memberCount === 1 ? '' : 'S'}` : null].filter(Boolean).join(' · ');
const EVENT_PILL = { going: ['GOING', '#22C55E', '#fff'], interested: ['INTERESTED', '#1F1F1F', '#fff'], hosting: ['HOSTING', '#fff', '#000'] };
const ROLE_PILL = { owner: 'OWNER', admin: 'ADMIN', member: 'MEMBER' };

function useCopied() {
  const [copied, setCopied] = useState(false);
  const t = useRef(null);
  useEffect(() => () => clearTimeout(t.current), []);
  return [copied, () => { setCopied(true); clearTimeout(t.current); t.current = setTimeout(() => setCopied(false), 1600); }];
}

/** The identity buttons: 46px pills, 11px/800/.14em, hover hot pink. `variant` primary | secondary | muted. */
function ActionButton({ a, phone }) {
  const bg = a.variant === 'primary' ? '#fff' : a.variant === 'muted' ? '#000' : '#1F1F1F';
  const fg = a.variant === 'primary' ? '#000' : a.variant === 'muted' ? '#8A8A8A' : '#fff';
  return (
    <button
      type="button"
      onClick={a.onClick}
      disabled={a.disabled}
      aria-disabled={a.muted ? 'true' : undefined}
      className={`pk-btn ${a.variant === 'muted' ? '' : 'pp-action'}`}
      style={{ height: 46, padding: phone ? '0 8px' : '0 20px', flex: phone ? '1 1 0' : undefined, minWidth: 0, borderRadius: 999, background: bg, color: fg, fontSize: phone ? 10 : 11, fontWeight: 800, letterSpacing: phone ? '.1em' : '.14em', textTransform: 'uppercase', cursor: a.muted ? 'default' : 'pointer', opacity: 1 }}
    >
      {a.label}
    </button>
  );
}

function Facts({ d, size, phone }) {
  const placeLine = [d.location, d.genderHidden ? '' : genderOnPage(d.gender)].filter(Boolean).join(' · ');
  const row = { display: 'flex', alignItems: 'center', gap: 12 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: phone ? 9 : 10, fontSize: phone ? 12 : 13 }}>
      {d.occupation && <span style={row} data-testid="fact-occupation">{Icon.work(size)}<span style={{ overflowWrap: 'anywhere' }}>{d.occupation}</span></span>}
      {placeLine && <span style={row}>{Icon.pin(size)}{placeLine}</span>}
      {d.since && <span style={row}>{Icon.cal(size)}Member since {d.since}</span>}
      {d.instagram && (
        <a href={`https://instagram.com/${d.instagram}`} target="_blank" rel="noreferrer" className="pk-link-white" style={{ ...row, textDecoration: 'underline', textUnderlineOffset: 3 }}>{Icon.ig(size)}@{d.instagram}</a>
      )}
      {d.tiktok && (
        <a href={`https://www.tiktok.com/@${d.tiktok}`} target="_blank" rel="noreferrer" className="pk-link-white" data-testid="fact-tiktok" style={{ ...row, textDecoration: 'underline', textUnderlineOffset: 3 }}>{Icon.tt(size)}@{d.tiktok}</a>
      )}
    </div>
  );
}

function NameBlock({ d, fs, vibeFs, gap = 8, vibeTop = 0 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      <h1 style={{ margin: 0, fontFamily: SERIF, fontWeight: 700, fontSize: fs, lineHeight: 1, letterSpacing: '.01em', textTransform: 'uppercase', textWrap: 'balance', overflowWrap: 'anywhere' }}>{d.name}</h1>
      {d.handle && <span style={{ fontSize: fs >= 38 ? 13 : 12, color: '#FF66C4', overflowWrap: 'anywhere' }}>@{d.handle}</span>}
      {d.vibe && <p style={{ margin: `${vibeTop}px 0 0`, fontFamily: SERIF, fontStyle: 'italic', fontSize: vibeFs, lineHeight: 1.3, color: '#E5E5E5', textWrap: 'pretty' }}>“{d.vibe}”</p>}
    </div>
  );
}

/** One cover photo. With `onOpen` it is a button into the media viewer (the +N tile opens there too). */
const Tile = ({ t, p, gap, dark, radius = 0, onOpen }) => {
  const Inner = onOpen ? 'button' : 'div';
  const props = onOpen ? { type: 'button', onClick: () => onOpen(t.i), 'aria-label': t.more > 0 ? `${t.more} more photos` : `Photo ${t.i + 1}`, 'data-testid': 'cover-tile' } : {};
  return (
    <div style={{ position: 'absolute', left: `${t.x}%`, top: `${t.y}%`, width: `${t.w}%`, height: `${t.h}%`, padding: tilePad(t, gap), boxSizing: 'border-box', background: dark ? '#000' : '#111' }}>
      <Inner {...props} style={{ position: 'relative', display: 'block', width: '100%', height: '100%', padding: 0, border: 'none', overflow: 'hidden', borderRadius: radius, background: dark ? '#111' : '#000', color: '#fff', cursor: onOpen ? 'pointer' : undefined, fontFamily: 'inherit' }}>
        <img src={p.src} alt="" referrerPolicy="no-referrer" draggable="false" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${p.fx}% ${p.fy}%`, display: 'block' }} />
        {t.more > 0 && (
          <span style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: dark ? 'rgba(0,0,0,.6)' : 'rgba(0,0,0,.66)' }}>
            <span style={{ fontSize: dark ? 26 : 30, fontWeight: 800 }}>+{t.more}</span>
            {!dark && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.16em' }}>PHOTOS</span>}
          </span>
        )}
      </Inner>
    </div>
  );
};

const Initial = ({ name, size }) => (
  <div aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: SERIF, fontSize: size, fontWeight: 700, color: '#333' }}>{(name || '?').charAt(0).toUpperCase()}</div>
);

/** A section card: radius 28 (22 on a phone), #111, the inset hairline. */
function Card({ title, count, link, onLink, phone, children, style, col }) {
  return (
    <section style={{ gridColumn: col, display: 'flex', flexDirection: 'column', gap: 14, padding: phone ? 18 : '22px 24px', borderRadius: phone ? 22 : 28, background: '#111', boxShadow: 'inset 0 0 0 1px #1F1F1F', minWidth: 0, ...style }}>
      {title && (
        <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.18em', color: '#8A8A8A' }}>{title}</span>
          {count != null && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#6B6B6B' }}>{count}</span>}
          {link && <button type="button" onClick={onLink} className="pk-btn" style={{ background: 'none', padding: 0, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', borderRadius: 0 }}>{link}</button>}
        </span>
      )}
      {children}
    </section>
  );
}

const chipStyle = (bg, fg, ring) => ({ display: 'flex', alignItems: 'center', minHeight: 34, padding: '6px 14px', boxSizing: 'border-box', borderRadius: 999, background: bg, color: fg, boxShadow: ring || 'none', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', lineHeight: 1.4, textTransform: 'uppercase' });

function Chips({ items, bg, fg, ring }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{items.map((c) => <span key={c} style={chipStyle(bg, fg, ring)}>{c}</span>)}</div>;
}

function Rows({ items, phone, kind, onOpen }) {
  const cols = phone ? '64px minmax(0,1fr) auto' : '88px minmax(0,1fr) auto';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((e) => {
        const isClub = kind === 'club';
        const key = isClub ? e.clubId : e.eventId;
        const img = isClub ? (e.logoUrl || e.coverUrl) : e.posterUrl;
        const [pill, pillBg, pillFg] = isClub ? [ROLE_PILL[e.role] || 'MEMBER', '#fff', '#000'] : (EVENT_PILL[e.relationship] || EVENT_PILL.going);
        const contain = isClub && !!e.logoUrl;
        return (
          <button key={key} type="button" onClick={() => onOpen && onOpen(e)} className="pk-btn pk-row-hover" style={{ display: 'grid', gridTemplateColumns: cols, gap: 14, alignItems: 'center', padding: '8px 14px 8px 8px', borderRadius: 20, background: '#000', color: '#fff', textAlign: 'left', whiteSpace: 'normal', width: '100%' }} data-testid={`${kind}-row`}>
            {img ? (
              <img src={img} alt="" referrerPolicy="no-referrer" style={{ width: '100%', aspectRatio: '1', objectFit: contain ? 'contain' : 'cover', borderRadius: 14, background: contain ? '#fff' : '#161616', display: 'block' }} />
            ) : (
              <span aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', aspectRatio: '1', borderRadius: 14, background: '#161616', fontFamily: SERIF, fontSize: 24, fontWeight: 700, color: '#444' }}>{(isClub ? e.name : e.title || '?').charAt(0).toUpperCase()}</span>
            )}
            <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{isClub ? e.name : e.title}</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', lineHeight: 1.6, color: '#8A8A8A', textTransform: 'uppercase' }}>{isClub ? clubMeta(e) : eventMeta(e)}</span>
            </span>
            <span style={{ padding: '6px 12px', borderRadius: 999, background: pillBg, color: pillFg, fontSize: 9, fontWeight: 800, letterSpacing: '.14em' }}>{pill}</span>
          </button>
        );
      })}
    </div>
  );
}

function Empty({ text, cta, onCta }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12, padding: 18, borderRadius: 20, background: '#000' }}>
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.16em', color: '#8A8A8A' }}>{text}</span>
      {cta && <button type="button" onClick={onCta} className="pk-btn pk-primary-on-black" style={{ height: 38, padding: '0 16px', fontSize: 10, fontWeight: 800, letterSpacing: '.14em' }}>{cta}</button>}
    </div>
  );
}

/** The share icon in the top row: a popover with the link, COPY and (where the device has it) SHARE…. */
function SharePopover({ handle, shareUrl }) {
  const [open, setOpen] = useState(false);
  const [copied, flash] = useCopied();
  const ref = useRef(null);
  const url = shareUrl || `https://${BRAND_DOMAIN}/${handle}`;
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const copy = async () => {
    try { await navigator.clipboard?.writeText(url); } catch { /* the link is right there to select */ }
    flash();
  };
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const share = async () => { try { await navigator.share({ url, title: handle ? `@${handle} on Portal` : 'Portal' }); setOpen(false); } catch { /* dismissed */ } };
  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Share this page" aria-haspopup="dialog" aria-expanded={open} className="pk-btn pk-secondary-on-black" style={{ width: 40, height: 40, padding: 0 }} data-testid="share-button">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M12 15V4M7 8l5-5 5 5M5 14v6h14v-6" /></svg>
      </button>
      {open && (
        <div role="dialog" aria-label="Share" data-testid="share-popover" style={{ position: 'absolute', right: 0, top: 48, zIndex: 30, width: 300, maxWidth: 'calc(100vw - 32px)', display: 'flex', flexDirection: 'column', gap: 10, padding: 16, borderRadius: 20, background: '#111', boxShadow: 'inset 0 0 0 1px #2A2A2A, 0 12px 40px rgba(0,0,0,.6)' }}>
          <span style={{ fontSize: 11, lineHeight: 1.6, color: '#8A8A8A' }}>{COPY_LINE}</span>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#22ECFB' }}>{BRAND_DOMAIN}/{handle}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={copy} className="pk-btn" style={{ flex: 1, height: 38, background: copied ? '#22ECFB' : '#fff', color: '#000', fontSize: 10, fontWeight: 800, letterSpacing: '.14em' }}>{copied ? 'COPIED' : 'COPY LINK'}</button>
            {canShare && <button type="button" onClick={share} className="pk-btn" style={{ flex: 1, height: 38, background: '#1F1F1F', color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.14em' }}>SHARE…</button>}
          </div>
        </div>
      )}
    </div>
  );
}

function ShareBlock({ handle, shareUrl, phone, title }) {
  const [copied, flash] = useCopied();
  const copy = async () => {
    try { await navigator.clipboard?.writeText(shareUrl || `https://${BRAND_DOMAIN}/${handle}`); } catch { /* the link is right there to select */ }
    flash();
  };
  return (
    <Card title={title} phone={phone} style={{ gap: 10, padding: 18 }}>
      <span style={{ fontSize: 11, lineHeight: 1.6, color: '#8A8A8A' }}>{COPY_LINE}</span>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#22ECFB' }}>{BRAND_DOMAIN}/{handle}</span>
        <button type="button" onClick={copy} className="pk-btn" style={{ flexShrink: 0, height: 30, padding: '0 14px', background: copied ? '#22ECFB' : '#fff', color: '#000', fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>{copied ? 'COPIED' : 'COPY'}</button>
      </div>
    </Card>
  );
}

export default function PortalPage({
  data: d,
  viewer = 'visitor',
  chrome = true,
  forceWidth,
  preview = false,
  guestlist = [],
  interested = [],
  hosting = [],
  clubs = [],
  actions = [],
  menu = null,
  shareUrl,
  onBack,
  onOpenEvent,
  onOpenClub,
  onHost,
  onEdit,
  initialTab = 'about',
}) {
  const rootRef = useRef(null);
  const w = useContainerWidth(rootRef, forceWidth);
  const phone = w < 900;
  const wide = w >= 1200;
  const twoCol = w >= 1000;
  const isSelf = viewer === 'self';

  const [tab, setTab] = useState(initialTab);
  const [cur, setCur] = useState(0);
  const [more, setMore] = useState(false);
  const [anthemKey, setAnthemKey] = useState(0);
  // The media viewer — story-style on a phone, a lightbox on desktop — over every cover photo.
  const [viewerIndex, setViewerIndex] = useState(null);
  const [copied, flashCopied] = useCopied();

  const photos = d.photos || [];
  const n = photos.length;
  const layout = phone ? mobileLayout(d.layoutM) : desktopLayout(d.layoutD);
  const contentW = Math.min(w, 1280) - (phone ? 32 : 64);
  const g = useMemo(() => geom(layout, n, phone, cur), [layout, n, phone, cur]);
  const gap = phone ? 3 : 4;
  const ratio = MOBILE_RATIO[layout] || 1;
  const coverH = phone ? Math.min(Math.round(contentW * ratio), 720) : (w >= 1100 ? 540 : 480);
  const textPx = (contentW * g.textW) / 100 - 80;
  const nameFs = textPx > 430 ? 46 : 38;
  const curIdx = n ? ((cur % n) + n) % n : 0;

  useEffect(() => { if (cur >= n && n) setCur(0); }, [cur, n]);

  const on = (k) => (d.sections || []).find((s) => s.k === k)?.on !== false;
  const allEvents = [...guestlist, ...interested];
  const hasTrack = !!(d.anthem && d.anthem.trackId);
  const down = (d.downFor || []).filter((x) => x !== SENSITIVE_CHIP || d.sensualVisible);
  const cap = phone ? 10 : Infinity;

  const tabs = [['about', 'ABOUT'], ['events', 'EVENTS']];
  if (on('guestlist') && allEvents.length) tabs.push(['guestlist', 'GUESTLIST']);
  if (on('clubs') && clubs.length) tabs.push(['clubs', `CLUBS · ${clubs.length}`]);
  const activeTab = tabs.some((t) => t[0] === tab) ? tab : 'about';

  const copyShare = async () => {
    try { await navigator.clipboard?.writeText(shareUrl || `https://${BRAND_DOMAIN}/${d.handle}`); } catch { /* label still flips */ }
    flashCopied();
  };
  // The share line shows the handle when there is one, else the path the page is reachable at.
  const shareLabel = d.handle || (shareUrl || '').replace(/^https?:\/\/[^/]+\//, '');
  const resolvedActions = actions.map((a) => (a.key === 'share' ? { ...a, label: copied ? 'LINK COPIED' : a.label, onClick: copyShare } : a));

  const half = twoCol ? 'span 1' : '1 / -1';
  const full = '1 / -1';
  const blocks = [];
  if (activeTab === 'about') {
    (d.sections || []).filter((s) => s.on !== false).forEach(({ k }) => {
      if (k === 'music' && ((d.music || []).some((m) => m && m.trim()) || hasTrack)) {
        blocks.push(
          <Card key="music" title="MUSIC THAT MOVES ME" phone={phone} col={full}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {hasTrack && (
                <button type="button" onClick={() => setAnthemKey((k2) => k2 + 1)} className="pk-btn pk-row-hover" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, padding: '6px 16px 6px 6px', background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A', color: '#fff', textAlign: 'left', whiteSpace: 'normal', justifyContent: 'flex-start' }} data-testid="anthem-row">
                  {d.anthem.albumArt ? <img src={d.anthem.albumArt} alt="" style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', display: 'block' }} /> : <span aria-hidden="true" style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#1DB954' }} />}
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.16em', color: '#1DB954' }}>ANTHEM</span>
                    <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.anthem.name} <span style={{ fontWeight: 400, color: '#8A8A8A' }}>· {d.anthem.artist}</span></span>
                  </span>
                  {Icon.play}
                </button>
              )}
              {(d.music || []).map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 16, padding: '10px 0', borderTop: i ? '1px solid #222' : 'none' }}>
                  <span style={{ fontSize: 11, color: '#6B6B6B' }}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={{ fontFamily: SERIF, fontSize: 24, lineHeight: 1.2, color: t ? '#fff' : '#444', overflowWrap: 'anywhere' }}>{t || '—'}</span>
                </div>
              ))}
            </div>
          </Card>,
        );
      }
      if (k === 'strengths' && d.strengths?.length) blocks.push(<Card key="strengths" title="STRENGTHS" phone={phone} col={half}><Chips items={d.strengths} bg="#fff" fg="#000" /></Card>);
      if (k === 'countOn' && d.countOn?.length) blocks.push(<Card key="countOn" title="COUNT ON ME FOR" phone={phone} col={half}><Chips items={d.countOn} bg="#FFCCE6" fg="#000" /></Card>);
      if (k === 'downFor' && down.length) {
        blocks.push(
          <Card key="downFor" title="DOWN FOR" count={String(down.length)} phone={phone} col={full}>
            <Chips items={more ? down : down.slice(0, cap)} bg="#000" fg="#fff" ring="inset 0 0 0 1px #333" />
            {down.length > cap && (
              <button type="button" onClick={() => setMore((m) => !m)} className="pk-btn" style={{ alignSelf: 'flex-start', height: 34, padding: '0 14px', background: '#1A1A1A', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>{more ? 'SHOW LESS' : `+ ${down.length - cap} MORE`}</button>
            )}
          </Card>,
        );
      }
      if (k === 'guestlist' && !wide && allEvents.length) {
        blocks.push(
          <Card key="guestlist" title="ON THE GUESTLIST" link={allEvents.length > 3 ? 'SEE ALL →' : null} onLink={() => setTab('guestlist')} phone={phone} col={full}>
            <Rows items={allEvents.slice(0, 3)} phone={phone} kind="event" onOpen={onOpenEvent} />
          </Card>,
        );
      }
      if (k === 'clubs' && clubs.length) blocks.push(<Card key="clubs" title="CLUBS" phone={phone} col={full}><Rows items={clubs} phone={phone} kind="club" onOpen={onOpenClub} /></Card>);
    });
    // A page nobody has filled in yet still reads as a page, not a hole.
    if (!blocks.length) {
      blocks.push(
        <Card key="empty" title="ABOUT" phone={phone} col={full}>
          <Empty text={isSelf ? 'YOUR PAGE IS EMPTY.' : `${(d.name || 'THEY').split(' ')[0].toUpperCase()} HASN'T FILLED THIS IN YET.`} cta={isSelf && !preview ? 'FILL IT IN →' : null} onCta={onEdit} />
        </Card>,
      );
    }
    if (!wide) blocks.push(<ShareBlock key="share" title="SHARE" handle={shareLabel} shareUrl={shareUrl} phone={phone} />);
  } else if (activeTab === 'events') {
    blocks.push(
      <Card key="hosting" title="HOSTING" count={hosting.length ? String(hosting.length) : null} phone={phone} col={full}>
        {hosting.length ? <Rows items={hosting} phone={phone} kind="event" onOpen={onOpenEvent} /> : <Empty text="NO EVENTS HOSTED YET." cta={isSelf && !preview ? 'HOST AN EVENT →' : null} onCta={onHost} />}
      </Card>,
    );
  } else if (activeTab === 'guestlist') {
    blocks.push(<Card key="guestlist-all" title="ON THE GUESTLIST" count={String(allEvents.length)} phone={phone} col={full}><Rows items={allEvents} phone={phone} kind="event" onOpen={onOpenEvent} /></Card>);
  } else {
    blocks.push(<Card key="clubs-all" title="CLUBS" count={String(clubs.length)} phone={phone} col={full}><Rows items={clubs} phone={phone} kind="club" onOpen={onOpenClub} /></Card>);
  }

  const isCarousel = phone && layout === 'carousel' && n > 1;
  const isBackdrop = phone && layout === 'backdrop' && n > 0;
  const nameInCard = !isBackdrop;
  const tiles = g.tiles.filter((t) => photos[t.i]);
  const openPhoto = preview ? undefined : (i) => setViewerIndex(i);
  const viewerItems = useMemo(() => photos.map((p) => ({ kind: 'image', url: p.src })), [photos]);

  return (
    <div ref={rootRef} className="pk" data-testid="portal-page" data-breakpoint={phone ? 'phone' : wide ? 'wide' : 'desktop'} style={{ position: 'relative', background: '#000', color: '#fff', minHeight: preview ? '100%' : undefined, height: preview ? '100%' : undefined, overflow: preview ? 'auto' : undefined, boxSizing: 'border-box' }}>
      {/* With an anthem the floating player sits over the bottom-right corner; the page ends 90px
          higher so the last card (SHARE, on a phone) is never under it. */}
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: phone ? `16px 16px ${hasTrack ? 114 : 24}px` : `28px 32px ${hasTrack ? 162 : 72}px`, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: phone ? 12 : 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <button type="button" onClick={onBack} className="pk-btn pk-secondary-on-black" style={{ height: 40, padding: '0 16px 0 12px', fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>{Icon.back}PEOPLE</button>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isSelf && <span style={{ padding: '6px 12px', borderRadius: 999, background: '#161616', color: '#8A8A8A', fontSize: 10, fontWeight: 700, letterSpacing: '.16em' }}>YOUR PORTAL PAGE</span>}
            {!preview && <SharePopover handle={shareLabel} shareUrl={shareUrl} />}
            {menu}
          </span>
        </div>

        {!phone && (
          <section data-testid="cover-desktop" data-layout={layout} style={{ position: 'relative', height: coverH, borderRadius: 28, overflow: 'hidden', background: '#111', boxShadow: 'inset 0 0 0 1px #1F1F1F' }}>
            {tiles.map((t) => <Tile key={`${t.i}-${t.x}`} t={t} p={photos[t.i]} gap={gap} onOpen={openPhoto} />)}
            {!n && <div style={{ position: 'absolute', left: '62%', top: 0, width: '38%', height: '100%' }}><Initial name={d.name} size={160} /></div>}
            {g.fade && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,#000 0%,rgba(0,0,0,.88) 36%,rgba(0,0,0,0) 70%)', pointerEvents: 'none' }} />}
            <div style={{ position: 'relative', zIndex: 2, width: `${g.textW}%`, height: '100%', padding: '36px 40px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 18, pointerEvents: 'none' }} className="pp-text-col">
              <NameBlock d={d} fs={nameFs} vibeFs={22} />
              <Facts d={d} size={16} />
              <div style={{ marginTop: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {resolvedActions.map((a) => (a.node ? <React.Fragment key={a.key}>{a.node}</React.Fragment> : <ActionButton key={a.key} a={a} />))}
              </div>
            </div>
          </section>
        )}

        {phone && (
          <>
            {n > 0 && (
              <section data-testid="cover-mobile" data-layout={layout} style={{ position: 'relative', height: coverH, borderRadius: 24, overflow: 'hidden', background: '#111' }}>
                {tiles.map((t) => <Tile key={`${t.i}-${t.x}`} t={t} p={photos[t.i]} gap={gap} dark onOpen={openPhoto} />)}
                {isCarousel && (
                  <>
                    <div style={{ position: 'absolute', left: 10, right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', justifyContent: 'space-between', pointerEvents: 'none' }}>
                      <button type="button" onClick={() => setCur((c) => (c + n - 1) % n)} aria-label="Previous photo" className="pk-btn" style={{ pointerEvents: 'auto', width: 40, height: 40, padding: 0, background: 'rgba(0,0,0,.6)', color: '#fff' }}>{Icon.prev}</button>
                      <button type="button" onClick={() => setCur((c) => (c + 1) % n)} aria-label="Next photo" className="pk-btn" style={{ pointerEvents: 'auto', width: 40, height: 40, padding: 0, background: 'rgba(0,0,0,.6)', color: '#fff' }}>{Icon.next}</button>
                    </div>
                    <div style={{ position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 5, padding: '6px 8px', borderRadius: 999, background: 'rgba(0,0,0,.6)' }}>
                      {photos.map((p, i) => (
                        <button key={p.id} type="button" onClick={() => setCur(i)} aria-label={`Go to photo ${i + 1}`} aria-current={i === curIdx ? 'true' : undefined} style={{ width: i === curIdx ? 18 : 6, height: 6, padding: 0, border: 'none', borderRadius: 999, background: i === curIdx ? '#fff' : 'rgba(255,255,255,.4)', cursor: 'pointer', transition: 'width .2s, background-color .2s' }} />
                      ))}
                    </div>
                  </>
                )}
                {isBackdrop && (
                  <>
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(0,0,0,0) 40%,rgba(0,0,0,.92) 88%)', pointerEvents: 'none' }} />
                    <div style={{ position: 'absolute', left: 18, right: 18, bottom: 18, pointerEvents: 'none' }}><NameBlock d={d} fs={32} vibeFs={19} gap={6} vibeTop={4} /></div>
                  </>
                )}
              </section>
            )}
            <section data-testid="identity-card" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '18px 16px', borderRadius: 24, background: '#111', boxShadow: 'inset 0 0 0 1px #1F1F1F' }}>
              {nameInCard && <NameBlock d={d} fs={32} vibeFs={19} gap={6} vibeTop={6} />}
              <Facts d={d} size={15} phone />
              {resolvedActions.length > 0 && (
                <div style={{ display: 'flex', gap: 6 }}>
                  {resolvedActions.map((a) => (a.node ? <React.Fragment key={a.key}>{a.node}</React.Fragment> : <ActionButton key={a.key} a={a} phone />))}
                </div>
              )}
            </section>
          </>
        )}

        <div role="tablist" className="pk-scroll-x" style={{ display: 'flex', gap: 6 }}>
          {tabs.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={k === activeTab} onClick={() => setTab(k)} className="pk-btn" style={{ flexShrink: 0, height: 40, padding: '0 18px', border: '2px solid transparent', background: k === activeTab ? ON : '#111', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>{l}</button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: wide ? 'minmax(0,1fr) 320px' : 'minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
          <div style={{ display: 'grid', gridTemplateColumns: twoCol ? 'repeat(2,minmax(0,1fr))' : 'minmax(0,1fr)', gridAutoFlow: 'dense', gap: 16, minWidth: 0 }}>
            {blocks}
          </div>
          {wide && (
            <aside style={{ position: 'sticky', top: 96, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              {on('guestlist') && allEvents.length > 0 && (
                <Card title="ON THE GUESTLIST" phone={false} style={{ gap: 12, padding: 18 }}>
                  {allEvents.slice(0, 4).map((e) => (
                    <button key={e.eventId} type="button" onClick={() => onOpenEvent && onOpenEvent(e)} className="pk-btn pk-row-hover" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 12, padding: 8, borderRadius: 20, background: '#000', color: '#fff', textAlign: 'left', whiteSpace: 'normal' }} data-testid="rail-event">
                      <span style={{ display: 'block', width: '100%', aspectRatio: '4 / 3', borderRadius: 14, background: e.posterUrl ? `url(${e.posterUrl}) center/cover` : '#161616' }} aria-hidden="true" />
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 6px 6px' }}>
                        <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{e.title}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', lineHeight: 1.6, color: '#8A8A8A', textTransform: 'uppercase' }}>{eventMeta(e)}</span>
                      </span>
                    </button>
                  ))}
                  {allEvents.length > 4 && <button type="button" onClick={() => setTab('guestlist')} className="pk-btn pk-secondary-on-black" style={{ height: 38, fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>SEE ALL {allEvents.length} →</button>}
                </Card>
              )}
              <ShareBlock handle={shareLabel} shareUrl={shareUrl} phone={false} />
            </aside>
          )}
        </div>
      </main>

      {hasTrack && <ProfileAnthem anthem={d.anthem} chrome={chrome} phone={phone} openKey={anthemKey} preview={preview} />}
      {viewerIndex != null && viewerItems.length > 0 && (
        <MediaViewer items={viewerItems} index={viewerIndex} onIndex={setViewerIndex} onClose={() => setViewerIndex(null)} title={d.name} />
      )}
    </div>
  );
}
